import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { StateCreator } from 'zustand'

interface SidebarState {
  primary: {
    collapsed: boolean
    width: number // 64px collapsed, 240px expanded
    autoCollapse: boolean // Enable hover-to-expand behavior
    hoverExpanded: boolean // Temporary expansion state on hover
  }
  secondary: {
    collapsed: boolean
    width: number // 0px collapsed, 256px expanded
  }
  isMobile: boolean
  isTablet: boolean
}

interface SidebarActions {
  togglePrimary: () => void
  toggleSecondary: () => void
  setPrimaryCollapsed: (collapsed: boolean) => void
  setSecondaryCollapsed: (collapsed: boolean) => void
  setResponsiveState: (isMobile: boolean, isTablet: boolean) => void
  autoCollapsePrimary: () => void
  restorePrimary: () => void
  setAutoCollapse: (enabled: boolean) => void
  setHoverExpanded: (expanded: boolean) => void
}

type SidebarStore = SidebarState & SidebarActions

// SBR-COORD-001: Design System Compliant Widths (8pt grid)
const SIDEBAR_WIDTHS = {
  PRIMARY_COLLAPSED: 64, // 8×8
  PRIMARY_EXPANDED: 240, // 8×30
  SECONDARY_COLLAPSED: 0, // Hidden
  SECONDARY_EXPANDED: 256, // 8×32
} as const

// State validation with production error handling
const validateSidebarState = (state: SidebarState, actionName: string) => {
  const { primary, isMobile, isTablet } = state
  const warnings: string[] = []
  const criticalErrors: string[] = []

  // CRITICAL FIX: Add production error handling for invalid states
  try {
    // Critical validation 1: Ensure width is a valid number
    if (
      typeof primary.width !== 'number' ||
      isNaN(primary.width) ||
      primary.width < 0
    ) {
      criticalErrors.push(`Invalid width: ${primary.width}`)
      // Auto-fix: Set to collapsed width
      primary.width = SIDEBAR_WIDTHS.PRIMARY_COLLAPSED as number
    }

    // Critical validation 2: Ensure boolean values are actually booleans
    if (typeof primary.collapsed !== 'boolean') {
      criticalErrors.push(`Invalid collapsed state: ${primary.collapsed}`)
      // Auto-fix: Default to true
      primary.collapsed = true
    }

    if (typeof primary.autoCollapse !== 'boolean') {
      criticalErrors.push(`Invalid autoCollapse state: ${primary.autoCollapse}`)
      // Auto-fix: Default to false (safer)
      primary.autoCollapse = false
    }

    if (typeof primary.hoverExpanded !== 'boolean') {
      criticalErrors.push(
        `Invalid hoverExpanded state: ${primary.hoverExpanded}`
      )
      // Auto-fix: Default to false
      primary.hoverExpanded = false
    }

    // Critical validation 3: Ensure width matches design system constants
    if (
      ![
        SIDEBAR_WIDTHS.PRIMARY_COLLAPSED as number,
        SIDEBAR_WIDTHS.PRIMARY_EXPANDED as number,
      ].includes(primary.width)
    ) {
      criticalErrors.push(`Width ${primary.width} not in design system`)
      // Auto-fix: Set to appropriate width based on collapsed state
      primary.width = primary.collapsed
        ? (SIDEBAR_WIDTHS.PRIMARY_COLLAPSED as number)
        : (SIDEBAR_WIDTHS.PRIMARY_EXPANDED as number)
    }

    // Log critical errors in all environments
    if (criticalErrors.length > 0) {
      console.error(
        `[SidebarStore] CRITICAL state errors fixed after ${actionName}:`
      )
      criticalErrors.forEach(error => console.error(`  - ${error}`))
      console.error('State was auto-corrected to prevent crashes')
    }

    // Development-only detailed validation
    if (process.env.NODE_ENV === 'development') {
      // Validation 1: hoverExpanded should only be true when autoCollapse is enabled
      if (primary.hoverExpanded && !primary.autoCollapse) {
        warnings.push('hoverExpanded is true but autoCollapse is false')
      }

      // Validation 2: hoverExpanded should only be true on desktop
      if (primary.hoverExpanded && (isMobile || isTablet)) {
        warnings.push(
          'hoverExpanded is true on mobile/tablet (should be desktop only)'
        )
      }

      // Validation 3: Width should be consistent with collapsed state
      const expectedWidth = primary.collapsed
        ? SIDEBAR_WIDTHS.PRIMARY_COLLAPSED
        : SIDEBAR_WIDTHS.PRIMARY_EXPANDED
      if (primary.width !== expectedWidth && !primary.hoverExpanded) {
        warnings.push(
          `width (${primary.width}) doesn't match collapsed state (${primary.collapsed}). Expected: ${expectedWidth}`
        )
      }

      // Validation 4: Width should be expanded when hoverExpanded is true
      if (
        primary.hoverExpanded &&
        primary.width !== SIDEBAR_WIDTHS.PRIMARY_EXPANDED
      ) {
        warnings.push(
          `width (${primary.width}) should be expanded when hoverExpanded is true`
        )
      }

      // Validation 5: autoCollapse should be false on mobile/tablet
      if (primary.autoCollapse && (isMobile || isTablet)) {
        warnings.push(
          'autoCollapse is enabled on mobile/tablet (should be desktop only)'
        )
      }

      // Log warnings if any
      if (warnings.length > 0) {
        console.warn(
          `[SidebarStore] State validation warnings after ${actionName}:`
        )
        warnings.forEach(warning => console.warn(`  - ${warning}`))
        console.warn('Current state:', { primary, isMobile, isTablet })
      }
    }
  } catch (validationError) {
    console.error('[SidebarStore] State validation failed:', validationError)
    // Reset to safe defaults if validation itself fails
    primary.collapsed = true
    primary.width = SIDEBAR_WIDTHS.PRIMARY_COLLAPSED as number
    primary.autoCollapse = false
    primary.hoverExpanded = false
  }
}

// Create the store with proper persist middleware
const useSidebarStore = create<SidebarStore>()(
  persist(
    (set, get) => ({
      // Initial state
      primary: {
        collapsed: true, // Desktop default: collapsed when auto-collapse is enabled
        width: SIDEBAR_WIDTHS.PRIMARY_COLLAPSED,
        autoCollapse: true, // Enable auto-collapse by default on desktop
        hoverExpanded: false, // Not expanded by hover initially
      },
      secondary: {
        collapsed: false, // Default: visible
        width: SIDEBAR_WIDTHS.SECONDARY_EXPANDED,
      },
      isMobile: false,
      isTablet: false,

      // Actions
      togglePrimary: () => {
        const { primary } = get()
        const newState = {
          primary: {
            ...primary,
            collapsed: !primary.collapsed,
            width: primary.collapsed
              ? SIDEBAR_WIDTHS.PRIMARY_EXPANDED
              : SIDEBAR_WIDTHS.PRIMARY_COLLAPSED,
            hoverExpanded: false, // Reset hover state when manually toggling
          },
        }
        set(newState)
        validateSidebarState(get(), 'togglePrimary')
      },

      toggleSecondary: () => {
        const { secondary } = get()
        set({
          secondary: {
            collapsed: !secondary.collapsed,
            width: secondary.collapsed
              ? SIDEBAR_WIDTHS.SECONDARY_EXPANDED
              : SIDEBAR_WIDTHS.SECONDARY_COLLAPSED,
          },
        })
      },

      setPrimaryCollapsed: (collapsed: boolean) => {
        const { primary } = get()
        set({
          primary: {
            ...primary,
            collapsed,
            width: collapsed
              ? SIDEBAR_WIDTHS.PRIMARY_COLLAPSED
              : SIDEBAR_WIDTHS.PRIMARY_EXPANDED,
            hoverExpanded: false, // Reset hover state when manually setting collapsed
          },
        })
      },

      setSecondaryCollapsed: (collapsed: boolean) => {
        set({
          secondary: {
            collapsed,
            width: collapsed
              ? SIDEBAR_WIDTHS.SECONDARY_COLLAPSED
              : SIDEBAR_WIDTHS.SECONDARY_EXPANDED,
          },
        })
      },

      setResponsiveState: (isMobile: boolean, isTablet: boolean) => {
        const currentState = get()
        set({ isMobile, isTablet })

        // Auto-collapse behavior based on screen size
        if (isMobile) {
          // Mobile: Both sidebars hidden by default, disable auto-collapse
          set({
            primary: {
              ...currentState.primary,
              collapsed: true,
              width: SIDEBAR_WIDTHS.PRIMARY_COLLAPSED,
              autoCollapse: false, // Disable auto-collapse on mobile
              hoverExpanded: false,
            },
            secondary: {
              collapsed: true,
              width: SIDEBAR_WIDTHS.SECONDARY_COLLAPSED,
            },
          })
        } else if (isTablet) {
          // Tablet: Auto-collapse primary, secondary user-controlled, disable auto-collapse
          set({
            primary: {
              ...currentState.primary,
              collapsed: true,
              width: SIDEBAR_WIDTHS.PRIMARY_COLLAPSED,
              autoCollapse: false, // Disable auto-collapse on tablet
              hoverExpanded: false,
            },
            // Keep secondary state as is
          })
        } else {
          // Desktop: Restore previous state or defaults, preserve user auto-collapse preference
          if (currentState.isMobile || currentState.isTablet) {
            set({
              primary: {
                ...currentState.primary,
                // If auto-collapse is enabled, start collapsed; otherwise preserve user preference
                collapsed: currentState.primary.autoCollapse
                  ? true
                  : currentState.primary.collapsed,
                width: currentState.primary.autoCollapse
                  ? SIDEBAR_WIDTHS.PRIMARY_COLLAPSED
                  : currentState.primary.collapsed
                    ? SIDEBAR_WIDTHS.PRIMARY_COLLAPSED
                    : SIDEBAR_WIDTHS.PRIMARY_EXPANDED,
                // Preserve user's auto-collapse preference instead of forcing it
                autoCollapse: currentState.primary.autoCollapse,
                hoverExpanded: false,
              },
              // Keep secondary state as user preference
            })
          }
        }
        validateSidebarState(get(), 'setResponsiveState')
      },

      autoCollapsePrimary: () => {
        const { primary } = get()
        set({
          primary: {
            ...primary,
            collapsed: true,
            width: SIDEBAR_WIDTHS.PRIMARY_COLLAPSED,
            hoverExpanded: false,
          },
        })
      },

      restorePrimary: () => {
        const { isMobile, isTablet, primary } = get()
        if (!isMobile && !isTablet) {
          set({
            primary: {
              ...primary,
              collapsed: false,
              width: SIDEBAR_WIDTHS.PRIMARY_EXPANDED,
              hoverExpanded: false,
            },
          })
        }
      },

      setAutoCollapse: (enabled: boolean) => {
        const { primary, isMobile, isTablet } = get()
        set({
          primary: {
            ...primary,
            autoCollapse: enabled,
            // Force collapsed state when enabling auto-collapse on desktop
            collapsed:
              enabled && !isMobile && !isTablet ? true : primary.collapsed,
            // Update width based on new collapsed state
            width:
              enabled && !isMobile && !isTablet
                ? SIDEBAR_WIDTHS.PRIMARY_COLLAPSED
                : primary.collapsed
                  ? SIDEBAR_WIDTHS.PRIMARY_COLLAPSED
                  : SIDEBAR_WIDTHS.PRIMARY_EXPANDED,
            hoverExpanded: false, // Reset hover state when changing auto-collapse
          },
        })
        validateSidebarState(get(), 'setAutoCollapse')
      },

      // SBR-COORD-008: Primary Sidebar Hover Expansion System
      setHoverExpanded: (expanded: boolean) => {
        const { primary, isMobile, isTablet } = get()
        // Only allow hover expansion if auto-collapse is enabled and not on mobile/tablet
        if (primary.autoCollapse && !isMobile && !isTablet) {
          set({
            primary: {
              ...primary,
              hoverExpanded: expanded,
              // Update width based on hover state - when auto-collapse is enabled,
              // expanded = true means show expanded, expanded = false means collapse
              width: expanded
                ? SIDEBAR_WIDTHS.PRIMARY_EXPANDED
                : SIDEBAR_WIDTHS.PRIMARY_COLLAPSED,
            },
          })
          validateSidebarState(get(), 'setHoverExpanded')
        }
      },
    }),
    {
      name: 'vrp-sidebar-state', // localStorage key
      partialize: (state: SidebarStore) => ({
        // Only persist user preferences, not responsive states
        primary: {
          // Don't persist collapsed state when auto-collapse is enabled
          // Auto-collapse should determine the initial collapsed state
          collapsed: state.primary.autoCollapse
            ? true
            : state.primary.collapsed,
          width: state.primary.autoCollapse
            ? SIDEBAR_WIDTHS.PRIMARY_COLLAPSED
            : state.primary.width,
          autoCollapse: state.primary.autoCollapse,
          // Don't persist hoverExpanded - it's a temporary state
          hoverExpanded: false,
        },
        secondary: {
          collapsed: state.secondary.collapsed,
          width: state.secondary.width,
        },
        // Don't persist isMobile/isTablet - these are detected on load
      }),
      // CRITICAL FIX: Add error handling for localStorage operations
      storage: {
        getItem: (name: string) => {
          try {
            const item = localStorage.getItem(name)
            return item ? JSON.parse(item) : null
          } catch (error: unknown) {
            console.warn(
              '[SidebarStore] Failed to read from localStorage:',
              error
            )
            return null
          }
        },
        setItem: (name: string, value: unknown): void => {
          try {
            localStorage.setItem(name, JSON.stringify(value))
          } catch (error: unknown) {
            console.warn(
              '[SidebarStore] Failed to write to localStorage:',
              error
            )
            // Try to clear some space by removing this specific item and retrying
            if (
              error instanceof DOMException &&
              error.name === 'QuotaExceededError'
            ) {
              try {
                localStorage.removeItem(name)
                localStorage.setItem(name, JSON.stringify(value))
                console.log(
                  '[SidebarStore] Successfully saved after clearing space'
                )
              } catch (retryError: unknown) {
                console.error(
                  '[SidebarStore] Failed to save even after clearing space:',
                  retryError
                )
              }
            }
          }
        },
        removeItem: (name: string): void => {
          try {
            localStorage.removeItem(name)
          } catch (error: unknown) {
            console.warn(
              '[SidebarStore] Failed to remove from localStorage:',
              error
            )
          }
        },
      },
      // Add custom hydration to ensure auto-collapse logic is applied
      onRehydrateStorage: () => (state: SidebarState | null) => {
        if (state) {
          // Migration logic for existing users
          const { primary } = state
          let migrationApplied = false

          // Migration 1: Handle missing autoCollapse property (legacy users)
          if (primary.autoCollapse === undefined) {
            primary.autoCollapse = false // Default to disabled for existing users
            migrationApplied = true
          }

          // Migration 2: Fix contradiction - autoCollapse:true but collapsed:false
          if (primary.autoCollapse && !primary.collapsed) {
            primary.collapsed = true
            primary.width = SIDEBAR_WIDTHS.PRIMARY_COLLAPSED as number
            migrationApplied = true
          }

          // Migration 3: Normalize width values to design system standards
          if (
            primary.width &&
            ![
              SIDEBAR_WIDTHS.PRIMARY_COLLAPSED,
              SIDEBAR_WIDTHS.PRIMARY_EXPANDED,
            ].includes(primary.width as 64 | 240)
          ) {
            primary.width = primary.collapsed
              ? SIDEBAR_WIDTHS.PRIMARY_COLLAPSED
              : SIDEBAR_WIDTHS.PRIMARY_EXPANDED
            migrationApplied = true
          }

          // Migration 4: Ensure hoverExpanded is always false on rehydration
          if (primary.hoverExpanded !== false) {
            primary.hoverExpanded = false
            migrationApplied = true
          }

          // Apply auto-collapse logic after migrations
          if (primary.autoCollapse) {
            // Force collapsed state when auto-collapse is enabled
            state.primary.collapsed = true
            state.primary.width = SIDEBAR_WIDTHS.PRIMARY_COLLAPSED as number
            state.primary.hoverExpanded = false
          }

          // Development validation after persistence restore and migration
          if (
            typeof window !== 'undefined' &&
            process.env.NODE_ENV === 'development'
          ) {
            console.log('🔄 Sidebar state rehydrated:', {
              autoCollapse: primary.autoCollapse,
              collapsed: primary.collapsed,
              width: primary.width,
              migrationApplied,
              persistenceApplied: primary.autoCollapse
                ? 'forced collapsed'
                : 'preserved',
            })
          }
        }
      },
    }
  )
)

export default useSidebarStore
